import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import {
  Search, X, TrendingUp, TrendingDown, AlertTriangle, ArrowDownWideNarrow,
  Filter, Wifi, Monitor, Stethoscope, Activity,
} from 'lucide-react';

/* ═══ MOCK DATA ═══ */
const BUDGET_DATA = [
  {
    id: 'PAZ-8821', complessita: 'Alta', erogatore: 'Coop. Salute Roma',
    budgetPrevisto: 12400, spesaAttuale: 11200, distretto: 'Distretto 1',
    durataGG: 142, dataInizio: '02/11/2025',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Medicazione avanzata', qta: 48, costoUnit: 85, totale: 4080 },
      { desc: 'Accesso Infermieristico - Parametri vitali', qta: 36, costoUnit: 45, totale: 1620 },
      { desc: 'Fisioterapia motoria domiciliare', qta: 24, costoUnit: 95, totale: 2280 },
      { desc: 'Visita Medico Specialista (Geriatra)', qta: 6, costoUnit: 120, totale: 720 },
      { desc: 'OSS - Igiene e mobilizzazione', qta: 60, costoUnit: 35, totale: 2100 },
    ],
    assets: [
      { desc: 'Noleggio Kit Saturimetro + ECG', canone: '€ 45/mese', totale: 225 },
      { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 150 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 5700 },
      { name: 'Fisioterapia', value: 2280 },
      { name: 'OSS', value: 2100 },
      { name: 'Specialistica', value: 720 },
      { name: 'Telemedicina', value: 375 },
    ],
  },
  {
    id: 'PAZ-4410', complessita: 'Media', erogatore: 'Consorzio ADI Lazio',
    budgetPrevisto: 8200, spesaAttuale: 5740, distretto: 'Distretto 3',
    durataGG: 98, dataInizio: '10/01/2026',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Terapia EV', qta: 28, costoUnit: 75, totale: 2100 },
      { desc: 'Accesso Infermieristico - Prelievo', qta: 14, costoUnit: 45, totale: 630 },
      { desc: 'Fisioterapia respiratoria', qta: 16, costoUnit: 95, totale: 1520 },
      { desc: 'OSS - Assistenza pasti e igiene', qta: 30, costoUnit: 35, totale: 1050 },
      { desc: 'Educazione terapeutica caregiver', qta: 4, costoUnit: 60, totale: 240 },
    ],
    assets: [
      { desc: 'Noleggio Concentratore O2 portatile', canone: '€ 80/mese', totale: 280 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 2730 },
      { name: 'Fisioterapia', value: 1520 },
      { name: 'OSS', value: 1050 },
      { name: 'Educazione', value: 240 },
      { name: 'Telemedicina', value: 280 },
    ],
  },
  {
    id: 'PAZ-6677', complessita: 'Alta', erogatore: 'Fondazione Gemelli@Home',
    budgetPrevisto: 15000, spesaAttuale: 16800, distretto: 'Distretto 2',
    durataGG: 186, dataInizio: '18/09/2025',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Gestione CVC', qta: 90, costoUnit: 95, totale: 8550 },
      { desc: 'Nutrizione Parenterale domiciliare', qta: 62, costoUnit: 45, totale: 2790 },
      { desc: 'Fisioterapia neuromuscolare', qta: 36, costoUnit: 110, totale: 3960 },
      { desc: 'Visita Palliativista', qta: 8, costoUnit: 130, totale: 1040 },
    ],
    assets: [
      { desc: 'Pompa infusione nutrizionale', canone: '€ 120/mese', totale: 720 },
      { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 180 },
      { desc: 'Materasso antidecubito dinamico', canone: '€ 55/mese', totale: 330 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 8550 },
      { name: 'Nutrizione', value: 2790 },
      { name: 'Fisioterapia', value: 3960 },
      { name: 'Specialistica', value: 1040 },
      { name: 'Asset/Telemedicina', value: 1230 },
    ],
  },
  {
    id: 'PAZ-3301', complessita: 'Bassa', erogatore: 'Coop. Riabilita Roma',
    budgetPrevisto: 4800, spesaAttuale: 2160, distretto: 'Distretto 1',
    durataGG: 56, dataInizio: '22/02/2026',
    prestazioni: [
      { desc: 'Fisioterapia motoria post-chirurgica', qta: 16, costoUnit: 95, totale: 1520 },
      { desc: 'Accesso Infermieristico - Ferita chirurgica', qta: 8, costoUnit: 65, totale: 520 },
      { desc: 'Visita Fisiatra di controllo', qta: 2, costoUnit: 110, totale: 220 },
    ],
    assets: [],
    costiChart: [
      { name: 'Fisioterapia', value: 1520 },
      { name: 'Infermieristica', value: 520 },
      { name: 'Specialistica', value: 220 },
    ],
  },
  {
    id: 'PAZ-5590', complessita: 'Media', erogatore: 'ASL Roma 2 — Diretto',
    budgetPrevisto: 7600, spesaAttuale: 6840, distretto: 'Distretto 4',
    durataGG: 124, dataInizio: '15/12/2025',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Insulinoterapia', qta: 60, costoUnit: 40, totale: 2400 },
      { desc: 'Accesso Infermieristico - Medicazione piede', qta: 24, costoUnit: 85, totale: 2040 },
      { desc: 'Educazione terapeutica diabetologo', qta: 6, costoUnit: 80, totale: 480 },
      { desc: 'OSS - Igiene e mobilizzazione', qta: 40, costoUnit: 35, totale: 1400 },
    ],
    assets: [
      { desc: 'Kit Telemonitoraggio Glicemia', canone: '€ 35/mese', totale: 140 },
      { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 120 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 4440 },
      { name: 'Educazione', value: 480 },
      { name: 'OSS', value: 1400 },
      { name: 'Telemedicina', value: 260 },
    ],
  },
  {
    id: 'PAZ-7712', complessita: 'Alta', erogatore: 'Coop. Salute Roma',
    budgetPrevisto: 18000, spesaAttuale: 17100, distretto: 'Distretto 2',
    durataGG: 210, dataInizio: '20/08/2025',
    prestazioni: [
      { desc: 'Accesso Infermieristico - Gestione PEG', qta: 96, costoUnit: 75, totale: 7200 },
      { desc: 'Accesso Infermieristico - Aspirazione tracheale', qta: 48, costoUnit: 85, totale: 4080 },
      { desc: 'Fisioterapia respiratoria intensiva', qta: 30, costoUnit: 110, totale: 3300 },
      { desc: 'Visita Pneumologo', qta: 4, costoUnit: 130, totale: 520 },
      { desc: 'Visita Nutrizionista clinico', qta: 6, costoUnit: 100, totale: 600 },
    ],
    assets: [
      { desc: 'Ventilatore domiciliare BiPAP', canone: '€ 180/mese', totale: 1260 },
      { desc: 'Aspiratore tracheale portatile', canone: '€ 40/mese', totale: 280 },
      { desc: 'Canone Piattaforma Telemonitoraggio', canone: '€ 30/mese', totale: 210 },
    ],
    costiChart: [
      { name: 'Infermieristica', value: 11280 },
      { name: 'Fisioterapia', value: 3300 },
      { name: 'Specialistica', value: 1120 },
      { name: 'Asset/Telemedicina', value: 1750 },
    ],
  },
  {
    id: 'PAZ-2204', complessita: 'Bassa', erogatore: 'Consorzio ADI Lazio',
    budgetPrevisto: 3200, spesaAttuale: 3450, distretto: 'Distretto 3',
    durataGG: 72, dataInizio: '05/02/2026',
    prestazioni: [
      { desc: 'Fisioterapia domiciliare', qta: 20, costoUnit: 95, totale: 1900 },
      { desc: 'Accesso Infermieristico - Parametri', qta: 12, costoUnit: 45, totale: 540 },
      { desc: 'OSS - Supporto deambulazione', qta: 20, costoUnit: 35, totale: 700 },
      { desc: 'Visita Fisiatra', qta: 3, costoUnit: 110, totale: 330 },
    ],
    assets: [],
    costiChart: [
      { name: 'Fisioterapia', value: 1900 },
      { name: 'Infermieristica', value: 540 },
      { name: 'OSS', value: 700 },
      { name: 'Specialistica', value: 330 },
    ],
  },
];

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6'];

const COMP_STYLE = {
  Alta:  'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Media: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Bassa: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
};

function absorptionColor(pct) {
  if (pct > 100) return { bar: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: 'Over-budget' };
  if (pct > 90)  return { bar: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: 'Critico' };
  if (pct > 70)  return { bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: 'Attenzione' };
  return { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'In linea' };
}

export default function ASLBudgetSalute() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('tutti');

  const filtered = useMemo(() => {
    let list = [...BUDGET_DATA];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => r.id.toLowerCase().includes(q) || r.erogatore.toLowerCase().includes(q) || r.distretto.toLowerCase().includes(q));
    }
    if (filter === 'top-spender') {
      list.sort((a, b) => b.spesaAttuale - a.spesaAttuale);
    } else if (filter === 'alert') {
      list = list.filter(r => r.spesaAttuale > r.budgetPrevisto);
    }
    return list;
  }, [search, filter]);

  const totBudget = BUDGET_DATA.reduce((s, r) => s + r.budgetPrevisto, 0);
  const totSpesa = BUDGET_DATA.reduce((s, r) => s + r.spesaAttuale, 0);
  const overBudgetCount = BUDGET_DATA.filter(r => r.spesaAttuale > r.budgetPrevisto).length;

  return (
    <AppLayout role="asl">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="asl-budget-salute-page">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Monitoraggio Budget di Salute</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Analisi granulare dei costi e assorbimento budget per singolo percorso</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Budget Totale</p>
              <p className="text-sm font-bold text-slate-800 dark:text-white">€ {totBudget.toLocaleString()}</p>
            </div>
            <div className="text-right px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Spesa YTD</p>
              <p className={`text-sm font-bold ${totSpesa > totBudget ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>€ {totSpesa.toLocaleString()}</p>
            </div>
            {overBudgetCount > 0 && (
              <Badge className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-0 text-xs font-bold gap-1">
                <AlertTriangle className="w-3 h-3" /> {overBudgetCount} over-budget
              </Badge>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per ID paziente, erogatore o distretto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-xl text-sm"
              data-testid="budget-search"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setFilter('tutti')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === 'tutti' ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
              data-testid="filter-tutti"
            >
              Tutti ({BUDGET_DATA.length})
            </button>
            <button
              onClick={() => setFilter('top-spender')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === 'top-spender' ? 'bg-violet-600 text-white' : 'bg-violet-50 dark:bg-violet-950/20 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/30'}`}
              data-testid="filter-top-spender"
            >
              <ArrowDownWideNarrow className="w-3 h-3" /> Top Spender
            </button>
            <button
              onClick={() => setFilter('alert')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === 'alert' ? 'bg-red-600 text-white' : 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30'}`}
              data-testid="filter-alert"
            >
              <AlertTriangle className="w-3 h-3" /> Alert Scostamento ({overBudgetCount})
            </button>
          </div>
        </div>

        {/* Data Grid */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden" data-testid="budget-table">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700/60">
                  {['ID Paziente', 'Ente Erogatore', 'Budget Previsto', 'Spesa Attuale', 'Stato Assorbimento', ''].map((col, i) => (
                    <th key={i} className="px-5 py-3 text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-400">Nessun percorso trovato</td></tr>
                ) : (
                  filtered.map(row => {
                    const pct = Math.round((row.spesaAttuale / row.budgetPrevisto) * 100);
                    const style = absorptionColor(pct);
                    const isOver = row.spesaAttuale > row.budgetPrevisto;
                    return (
                      <tr
                        key={row.id}
                        className={`border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors cursor-pointer ${isOver ? 'bg-red-50/30 dark:bg-red-950/10' : ''}`}
                        onClick={() => navigate(`/asl/budget-salute/audit/${row.id}`)}
                        data-testid={`row-${row.id}`}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-slate-800 dark:text-white">{row.id}</p>
                            <Badge className={`${COMP_STYLE[row.complessita]} border-0 text-[9px] font-bold`}>{row.complessita}</Badge>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{row.distretto} &middot; {row.durataGG}gg</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{row.erogatore}</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-slate-800 dark:text-white">€ {row.budgetPrevisto.toLocaleString()}</p>
                        </td>
                        <td className="px-5 py-4">
                          <p className={`text-sm font-bold ${style.text}`}>€ {row.spesaAttuale.toLocaleString()}</p>
                          {isOver && <p className="text-[10px] text-red-500 font-medium mt-0.5">+€ {(row.spesaAttuale - row.budgetPrevisto).toLocaleString()} over</p>}
                        </td>
                        <td className="px-5 py-4 min-w-[200px]">
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all duration-500 ${style.bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                              </div>
                            </div>
                            <span className={`text-xs font-bold shrink-0 w-10 text-right ${style.text}`}>{pct}%</span>
                          </div>
                          <p className={`text-[10px] font-medium mt-0.5 ${style.text}`}>{style.label}</p>
                        </td>
                        <td className="px-5 py-4">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/asl/budget-salute/audit/${row.id}`); }} className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors" data-testid={`drill-${row.id}`}>Audit</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
